import * as UI from "@iyk/ui"
import { HlsVideo } from "@iyk/vf-ui"
import * as React from "react"

import { ExclusiveCard } from "./exclusive-card.tsx"

export const ExclusiveVideoCard = (props: ExclusiveVideoCard.Props) => {
  const videoRef = React.useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = React.useState(false)
  const [isInPiP, setIsInPiP] = React.useState(false)

  const handlePlay = async () => {
    const video = videoRef.current
    if (!video || isPlaying) return

    try {
      await video.play()
      props.onPlay()
    } catch (err) {
      console.error("Error when playing video", err)
    }
  }

  React.useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const controller = new AbortController()

    const handlePlaying = () => setIsPlaying(true)

    const handlePause = () => {
      if (video.paused && !video.seeking && !video.ended) {
        setIsPlaying(false)
      }
    }

    const handleEnded = () => setIsPlaying(false)
    const handleEnterPiP = () => setIsInPiP(true)
    const handleLeavePiP = () => setIsInPiP(false)

    video.addEventListener("playing", handlePlaying, { signal: controller.signal })
    video.addEventListener("pause", handlePause, { signal: controller.signal })
    video.addEventListener("ended", handleEnded, { signal: controller.signal })
    video.addEventListener("enterpictureinpicture", handleEnterPiP, { signal: controller.signal })
    video.addEventListener("leavepictureinpicture", handleLeavePiP, { signal: controller.signal })

    return () => controller.abort()
  }, [])

  const showUI = !isPlaying || isInPiP

  return (
    <div onClick={handlePlay}>
      <ExclusiveCard.Container>
        <HlsVideo
          ref={videoRef}
          src={props.videoSrc}
          poster={props.coverImage}
          className={UI.cx(
            "absolute inset-0 size-full object-cover transition-opacity duration-300",
            isPlaying ? "opacity-100 pointer-events-auto z-10" : "opacity-0 pointer-events-none z-0"
          )}
          controls
        />

        <div
          className={UI.cx(
            "absolute inset-0 size-full transition-opacity duration-300 z-10",
            showUI ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
          )}
        >
          <ExclusiveCard.CoverImage src={props.coverImage} alt={props.title} />
          <ExclusiveCard.IconButton>
            <UI.Icon.Play />
          </ExclusiveCard.IconButton>
          <ExclusiveCard.Heading>
            <ExclusiveCard.Title>{props.title}</ExclusiveCard.Title>
            <ExclusiveCard.Subtitle>{props.subtitle}</ExclusiveCard.Subtitle>
          </ExclusiveCard.Heading>
        </div>
      </ExclusiveCard.Container>
    </div>
  )
}

namespace ExclusiveVideoCard {
  export type Props = {
    id: string
    title: string
    subtitle?: string
    coverImage: string
    videoSrc: string
    autoPlay?: boolean
    onPlay: () => void
  }
}
